import {
  Button,
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { emptyPaginationResponse } from '@duke-hq/lib/pagination';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ArrowRightFromLine, Plus } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { trpcAdmin } from '../lib/trpcAdmin';
import { EmployerStatusBadge } from './components/EmployerStatusBadge';
import { EmployerFilters, useEmployerFilters } from './components/EmployerFilters';
import { AddEmployerModal } from './AddEmployerModal';
import { EmployerDetailPage } from './EmployerDrawer';
import { searchSuffix } from './searchSuffix';
import { testIds } from './testIds';

export function Employers(): React.JSX.Element {
  const { pageSize, currentPage, setPage } = usePagination();
  const [searchParams] = useSearchParams();
  const querySearch = (searchParams.get('search') ?? '').trim();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { statuses, states } = useEmployerFilters();
  const hasFilters = statuses.length > 0 || states.length > 0;
  const addEmployer = useDisclosure();

  const { employerId } = useParams();
  const navigate = useNavigate();
  const querySuffix = searchSuffix(searchParams);
  const openEmployer = (id: string) => navigate(`/placements/employers/${id}${querySuffix}`);
  const closeDrawer = () => navigate(`/placements/employers${querySuffix}`);

  const { data, isFetching } = trpcAdmin.workPlacements.employers.listEmployers.useQuery(
    {
      search: querySearch === '' ? undefined : querySearch,
      filters: hasFilters ? { statuses, states } : undefined,
      pagination: { page: currentPage, limit: pageSize },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  return (
    <>
      <TableViewLayout
        pageHeader={[
          !isExpanded && (
            <IconButton
              aria-label={t`expand secondary sidebar`}
              key="toggle-expand-sub-sidebar"
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
              icon={<ArrowRightFromLine />}
            />
          ),
          <PageHeading key="page-heading">
            <Trans>Employers</Trans>
          </PageHeading>,
        ]}
        pageActions={
          <Button
            key="add-employer"
            leftIcon={<Plus size={iconSize.sm} />}
            variant="primary"
            size="sm"
            onClick={addEmployer.onOpen}
            data-testid={testIds.addEmployerTrigger}
          >
            <Trans>Add new employer</Trans>
          </Button>
        }
        filters={
          <>
            <SearchInput maxW="search-input" />
            <EmployerFilters />
          </>
        }
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={data.pagination.totalCount || 0}
              zero="0 employers"
              one="1 employer"
              other="# employers"
            />
          </Text>
        }
        table={
          <>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>
                    <Trans>Employer</Trans>
                  </Th>
                  <Th>
                    <Trans>Contact</Trans>
                  </Th>
                  <Th>
                    <Trans>Status</Trans>
                  </Th>
                  <Th isNumeric>
                    <Trans>Cap</Trans>
                  </Th>
                  <Th isNumeric>
                    <Trans>Current</Trans>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.items.map((row) => (
                  <Tr
                    key={row.id}
                    cursor="pointer"
                    _hover={{ bg: 'bg.subtle' }}
                    onClick={() => openEmployer(row.id)}
                    data-testid={testIds.employerRow}
                  >
                    <Td>
                      <Text>{row.name}</Text>
                    </Td>
                    <Td>
                      {row.contact != null ? (
                        <Stack gap={0}>
                          <Text>{`${row.contact.firstName} ${row.contact.lastName}`}</Text>
                          {row.contact.email != null ? (
                            <Text fontSize="xs" color="fg.subtle">
                              {row.contact.email}
                            </Text>
                          ) : null}
                        </Stack>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>
                      <EmployerStatusBadge status={row.status} />
                    </Td>
                    <Td isNumeric>{row.cap ?? '—'}</Td>
                    {/* Live placement counts land in DUKE-2349; until then show — */}
                    <Td isNumeric>—</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {isFetching ? (
              <Center p={8}>
                <Spinner size="xl" />
              </Center>
            ) : data.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No employers yet</Trans>
                </Text>
              </Center>
            ) : null}
          </>
        }
        pagination={
          data.items.length > 0 ? (
            <Pagination pagination={data.pagination} onPageChange={setPage} />
          ) : null
        }
      />
      <AddEmployerModal isOpen={addEmployer.isOpen} onClose={addEmployer.onClose} />

      {employerId != null ? (
        <Drawer isOpen onClose={closeDrawer} size="xl">
          <DrawerOverlay />
          <DrawerContent maxW={pxToRem(930)}>
            <DrawerCloseButton />
            <EmployerDetailPage
              employerId={employerId}
              row={data.items.find((employer) => employer.id === employerId)}
            />
          </DrawerContent>
        </Drawer>
      ) : null}
    </>
  );
}
